import Phaser from 'phaser'
import { t } from '../../i18n'
import type { JKGameMsgKey } from '../../i18n/zh-TW'
import { isBackendEnabled } from '../../services/JKGame_api'
import {
  apiAccountBattlepass,
  apiAccountProgress,
  apiAccountPull,
  apiAccountWallet,
  apiBattlepassTrack,
  apiClaimBattlepass,
  apiEconomyConfig,
  apiGachaPreview,
  getHandle,
  missionLabel,
  RARITY_COLOR,
  rarityLabel,
  type AccountProgress,
  type GachaItem,
  type WalletState,
} from '../../services/JKGame_economy'
import { mountGachaOverlay, type GachaOverlayHandle, type GachaResultRow } from '../../ui/JKGame_gachaOverlay'
import { audio } from '../JKGame_audio'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, prefersReducedMotion } from '../JKGame_ui'

const BP_KIND_KEY: Record<'skin' | 'material' | 'char', JKGameMsgKey> = {
  skin: 'bp.kind.skin',
  material: 'bp.kind.material',
  char: 'bp.kind.char',
}

// Gacha is a commercial collection surface, but gameplay rules stay backend-authoritative:
// odds, pity, spend validation, wallet mutation, and ownership all come from the server.
export class JKGame_GachaScene extends Phaser.Scene {
  private overlay?: GachaOverlayHandle
  private bpClaimable = 0
  private bpClaimedCount = 0
  private bpTotal = 0
  private sinceSsr = 0
  private pitySsr = 0
  private costGem = 160
  private lastGem = 0
  private busy = false
  private alive = true
  private handle: string | null = null
  private confirmOpen = false
  private readonly keyEsc = (): void => {
    if (this.confirmOpen) this.dismissConfirm()
    else this.scene.start('Menu')
  }
  private readonly keyPull = (): void => this.confirmKeyOrPull()

  constructor() {
    super('Gacha')
  }

  create(): void {
    this.alive = true
    this.busy = false
    this.confirmOpen = false
    this.handle = null
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.overlay?.destroy()
      this.overlay = undefined
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
      this.input.keyboard?.off('keydown-SPACE', this.keyPull)
      this.input.keyboard?.off('keydown-ENTER', this.keyPull)
    })
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc)
    this.input.keyboard?.off('keydown-SPACE', this.keyPull).on('keydown-SPACE', this.keyPull)
    this.input.keyboard?.off('keydown-ENTER', this.keyPull).on('keydown-ENTER', this.keyPull)

    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(240, 0, 0, 0)
    const real = isBackendEnabled()
    this.overlay = mountGachaOverlay({
      title: t(real ? 'gacha.title' : 'gacha.titlePreview'),
      pitch: t('gacha.pitch'),
      loadingText: t('gacha.loading'),
      pullLabel: t(real ? 'gacha.pull10' : 'gacha.pull10Preview'),
      backLabel: t('menu.back'),
      cancelLabel: t('gacha.confirm.no'),
      confirmLabel: t('gacha.confirm.yes'),
      emptyText: t('gacha.results.empty'),
      onPull: () => {
        audio.play('uiTap')
        this.requestPull()
      },
      onBack: () => {
        audio.play('uiTap')
        this.scene.start('Menu')
      },
      onClaimBattlepass: () => {
        audio.play('uiTap')
        void this.claimBp()
      },
    })

    void this.init()
  }

  private async init(): Promise<void> {
    this.overlay?.setStatus(t('gacha.loading'))
    this.overlay?.setPullState(true, false)
    if (isBackendEnabled()) {
      try {
        const handle = getHandle()
        try {
          const c = await apiEconomyConfig()
          if (!this.alive) return
          this.pitySsr = c.gacha.pity_ssr
          this.costGem = c.gacha.cost_gem
        } catch {
          // Config is best-effort here; wallet load decides whether real mode can proceed.
        }
        const wallet = await apiAccountWallet(handle)
        if (!this.alive) return
        this.handle = handle
        this.showBalance(wallet)
        this.overlay?.setStatus('')
        void this.loadProgress(handle)
        void this.loadBattlepass(handle)
      } catch {
        this.setOffline()
      }
      return
    }

    try {
      const c = await apiEconomyConfig()
      if (!this.alive) return
      this.costGem = c.gacha.cost_gem
      this.pitySsr = c.gacha.pity_ssr
      this.overlay?.setEconomy(t('gacha.cost', { n: this.costGem * 10 }))
      this.renderPity()
      this.overlay?.setStatus('')
    } catch {
      this.setOffline()
    }
  }

  private async loadProgress(handle: string): Promise<void> {
    try {
      const p = await apiAccountProgress(handle)
      if (!this.alive) return
      this.overlay?.setProgress(this.progressLines(p))
    } catch {
      if (!this.alive) return
      this.overlay?.setProgress([t('progress.offline')])
    }
  }

  private progressLines(p: AccountProgress): string[] {
    const lines = [t('result.seasonLv', { lv: p.season.level }), t('progress.daily')]
    for (const m of p.missions) {
      const name = missionLabel(m.id, m.title)
      const row = t('progress.mission', { name, cur: m.current, goal: m.goal })
      const reward = t('progress.reward', { coin: m.reward.coin ?? 0, xp: m.reward.xp ?? 0 })
      lines.push(m.done ? `✓ ${row}${reward}` : `· ${row}${reward}`)
    }
    return lines
  }

  private async loadBattlepass(handle: string): Promise<void> {
    let main = ''
    try {
      const bp = await apiAccountBattlepass(handle)
      if (!this.alive) return
      this.bpClaimable = bp.claimable.length
      main = this.bpClaimable > 0
        ? `${t('bp.title')}：${t('bp.claimable', { n: this.bpClaimable })}　▶ ${t('bp.claim')}`
        : `${t('bp.title')}：${t('bp.none')}`
      this.overlay?.setBattlepass(main, '', this.bpClaimable > 0)
    } catch {
      if (!this.alive) return
      this.bpClaimable = 0
      this.overlay?.setBattlepass(t('progress.offline'), '', false)
      return
    }

    try {
      const tr = await apiBattlepassTrack(handle)
      if (!this.alive) return
      this.bpClaimedCount = tr.claimed_count
      this.bpTotal = tr.track.length
      let track = t('bp.track', { claimed: this.bpClaimedCount, total: this.bpTotal })
      const next = tr.track.find((e) => !e.claimed && e.reward.item)
      if (next && next.reward.item) {
        const item = this.bpKindLabel(next.reward.item.kind)
        track += `　·　${t('bp.levelLine', { lv: next.level, coin: next.reward.coin ?? 0, item })}`
      }
      this.overlay?.setBattlepass(main, track, this.bpClaimable > 0)
    } catch {
      // Track detail is best-effort; the claim line already rendered.
    }
  }

  private bpKindLabel(kind: string): string {
    return t(BP_KIND_KEY[kind as keyof typeof BP_KIND_KEY] ?? 'bp.kind.other')
  }

  private async claimBp(): Promise<void> {
    if (this.busy || this.bpClaimable <= 0 || !this.handle) return
    this.busy = true
    this.overlay?.setBattlepass(t('bp.claiming'), '', false)
    try {
      const res = await apiClaimBattlepass(this.handle)
      if (!this.alive) return
      const items = res.claimed.filter((c) => c.reward.item).length
      this.overlay?.setBattlepass(t('bp.gained', { coin: res.gained.coin, items }), '', false)
      this.showBalance(res.wallet)
      this.bpClaimable = 0
      this.bpClaimedCount += res.claimed.length
      if (this.bpTotal > 0) this.overlay?.setBattlepass(t('bp.title'), t('bp.track', { claimed: this.bpClaimedCount, total: this.bpTotal }), false)
    } catch {
      if (!this.alive) return
      this.overlay?.setBattlepass(`${t('progress.offline')}　▶ ${t('bp.claim')}`, '', true)
    } finally {
      this.busy = false
    }
  }

  private showBalance(w: WalletState): void {
    this.overlay?.setEconomy(`${t('gacha.balance', { gem: w.gem, coin: w.coin })}　·　${t('gacha.cost', { n: this.costGem * 10 })}`)
    this.sinceSsr = w.since_ssr
    this.renderPity()
    this.lastGem = w.gem
    this.refreshPullAfford()
  }

  private refreshPullAfford(): void {
    const afford = !this.handle || this.lastGem >= this.costGem * 10
    this.overlay?.setPullState(afford, false)
  }

  private setOffline(): void {
    if (!this.alive) return
    this.overlay?.setStatus(t('gacha.offline'), 'bad')
    this.overlay?.setEconomy('')
    this.overlay?.setPity('', false)
    this.overlay?.setPullState(false, false)
  }

  private renderPity(): void {
    if (this.pitySsr <= 0) {
      this.overlay?.setPity('', false)
      return
    }
    const remaining = Math.max(0, this.pitySsr - this.sinceSsr)
    const ready = remaining <= 0
    this.overlay?.setPity(ready ? t('gacha.pityReady') : t('gacha.pity', { n: remaining }), ready)
  }

  private requestPull(): void {
    if (this.busy || this.confirmOpen) return
    const realCharge = this.handle !== null && this.lastGem >= this.costGem * 10
    if (realCharge) this.showPullConfirm()
    else void this.pull()
  }

  private confirmKeyOrPull(): void {
    if (this.confirmOpen) {
      this.dismissConfirm()
      void this.pull()
    } else {
      this.requestPull()
    }
  }

  private showPullConfirm(): void {
    const guaranteed = this.pitySsr > 0 && this.pitySsr - this.sinceSsr <= 10
    const message = t('gacha.confirm.q', { n: this.costGem * 10, bal: this.lastGem })
      + (guaranteed ? `\n${t('gacha.confirm.pity')}` : '')
    this.confirmOpen = true
    this.overlay?.showConfirm(message, () => {
      this.confirmOpen = false
      void this.pull()
    }, () => {
      this.confirmOpen = false
    })
  }

  private dismissConfirm(): void {
    this.overlay?.closeConfirm()
    this.confirmOpen = false
  }

  private async pull(): Promise<void> {
    if (this.busy) return
    this.busy = true
    this.overlay?.setPullState(this.lastGem >= this.costGem * 10 || !this.handle, true)
    this.overlay?.setStatus(t('gacha.pulling'))
    let offline = false
    try {
      const seed = (Date.now() >>> 0) || 1
      if (this.handle) {
        const res = await apiAccountPull(this.handle, seed, 10)
        if (!this.alive) return
        if (res.ok && res.results) {
          this.renderResults(res.results)
          this.showBalance(res.wallet)
          this.overlay?.setStatus('')
        } else {
          this.overlay?.setStatus(t('gacha.broke'), 'bad')
        }
      } else {
        const res = await apiGachaPreview(seed, 10, this.sinceSsr)
        if (!this.alive) return
        this.sinceSsr = res.since_ssr
        this.renderResults(res.results)
        this.renderPity()
        this.overlay?.setStatus('')
      }
    } catch {
      offline = true
      this.setOffline()
    } finally {
      this.busy = false
      if (this.alive && !offline) this.refreshPullAfford()
    }
  }

  private renderResults(items: GachaItem[]): void {
    const rows: GachaResultRow[] = items.map((it) => ({
      rarity: rarityLabel(it.rarity),
      name: it.name,
      color: RARITY_COLOR[it.rarity],
    }))
    this.overlay?.setResults(rows)

    const ssr = items.some((it) => it.rarity === 'SSR')
    const hasSr = items.some((it) => it.rarity === 'SR')
    audio.play(ssr ? 'success' : 'reveal')
    if (prefersReducedMotion()) return
    const flashColor = ssr ? 0xffce4d : hasSr ? 0xc9a3ff : 0xffffff
    const flash = this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, flashColor, ssr ? 0.46 : hasSr ? 0.28 : 0.14).setDepth(1000)
    this.tweens.add({ targets: flash, alpha: 0, duration: ssr ? 720 : 420, ease: 'Cubic.Out', onComplete: () => flash.destroy() })
  }
}
